// Aba "Regras do Mercado Livre" da pagina do canal (plano mapeamento-campos, F2).
// Cada regra que antes vivia em JSON, em /admin/settings ou no codigo tem aqui um cartao em portugues,
// com o valor atual, dica de uma linha e Salvar proprio. Grava por PATCH /api/channels/:slug/ml/rules
// (capacidade channel_rules.write). O bloco "Avancado" so aparece para quem tem settings.manage.
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiErrorMessage } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import {
  ADVANCED_SETTINGS,
  AI_PROMPT_MAX,
  ML_FREE_SHIPPING_FLOOR,
  colorRows,
  gtinPatch,
  gtinToDraft,
  shippingPatch,
  shippingToDraft,
  validateGtin,
  validateShipping,
  type GtinDraft,
  type ShippingDraft,
  UNPUBLISH_MODES,
  aiPromptPatch,
  validateAiPrompt,
  audiencePatch,
  audienceToRows,
  mapPatch,
  mapToRows,
  rowsToMap,
  type AudienceRow,
  type PairRow,
  autoPatch,
  autoToDraft,
  defaultsPatch,
  defaultsToDraft,
  minutesLabel,
  safetyPatch,
  termsPatch,
  termsToDraft,
  unpublishModeOf,
  validateAdvanced,
  validateAuto,
  validateDefaults,
  validateSafety,
  validateTerms,
  type AutoDraft,
  type DefaultsDraft,
  type MlRulesResponse,
  type RulesPatch,
  type TermsDraft,
} from "../../lib/ml-rules";
import { AGE_GROUPS, GENDERS, LISTING_TYPES, PKG_LABEL } from "../../lib/ml-channel";
import { Button } from "../ui/button";
import { Input, Textarea } from "../ui/input";
import { Label } from "../ui/label";
import { Select } from "../ui/select";
import { Skeleton } from "../ui/skeleton";
import { Switch } from "../ui/switch";
import { MlCategoryMapPanel } from "./MlCategoryMapDialog";

const rulesKey = (slug: string) => ["ml-rules", slug];

/** Cartao de uma regra: titulo, explicacao curta, campos e Salvar. */
function RuleCard({
  title,
  hint,
  dirty,
  invalid,
  saving,
  onSave,
  children,
}: {
  title: string;
  hint: string;
  dirty: boolean;
  invalid: boolean;
  saving: boolean;
  onSave: () => void;
  children: ReactNode;
}): JSX.Element {
  return (
    <form
      className="card stack"
      style={{ gap: "var(--sp-3)", minWidth: 0 }}
      onSubmit={(e) => {
        e.preventDefault();
        if (dirty && !invalid) onSave();
      }}
    >
      <div className="stack" style={{ gap: ".25rem" }}>
        <h3 className="card__title" style={{ margin: 0 }}>{title}</h3>
        <p className="field__hint" style={{ margin: 0 }}>{hint}</p>
      </div>
      {children}
      <div className="spread">
        <span className="field__hint">{dirty ? "Há mudanças não salvas." : "Tudo salvo."}</span>
        <Button type="submit" disabled={!dirty || invalid || saving} loading={saving}>Salvar</Button>
      </div>
    </form>
  );
}

function Err({ text }: { text?: string }): JSX.Element | null {
  return text ? <p className="field__hint" style={{ color: "var(--color-bad)", margin: 0 }}>{text}</p> : null;
}

function useRulesSave(slug: string, okText: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: RulesPatch) => (await api.patch(`/api/channels/${slug}/ml/rules`, patch)).data,
    onSuccess: () => {
      toast.success(okText);
      void qc.invalidateQueries({ queryKey: rulesKey(slug) });
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
}

// ---------------------------------------------------------------------------
function AutoPublishCard({ slug, data }: { slug: string; data: MlRulesResponse }): JSX.Element {
  const saved = autoToDraft(data.settings);
  const [d, setD] = useState<AutoDraft>(saved);
  useEffect(() => setD(autoToDraft(data.settings)), [data.settings]);
  const save = useRulesSave(slug, "Publicação automática salva");
  const errors = validateAuto(d);
  const dirty = JSON.stringify(d) !== JSON.stringify(saved);
  const minutes = Number(d.zeroPauseMin);
  return (
    <RuleCard
      title="Publicar sozinho"
      hint="O Vitrine sobe para o Mercado Livre, sem ninguém pedir, o produto ativo que tem estoque, preço e foto. Desligado, nada sobe sozinho."
      dirty={dirty}
      invalid={Object.keys(errors).length > 0}
      saving={save.isPending}
      onSave={() => save.mutate(autoPatch(d))}
    >
      <Switch checked={d.enabled} onChange={(e) => setD({ ...d, enabled: e.target.checked })} label={d.enabled ? "Publicação automática ligada" : "Publicação automática desligada"} />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="field">
          <Label htmlFor="mlr-zero">Pausar o anúncio depois de quantos minutos sem estoque</Label>
          <Input id="mlr-zero" type="number" inputMode="numeric" min={0} step={1} value={d.zeroPauseMin} onChange={(e) => setD({ ...d, zeroPauseMin: e.target.value })} aria-invalid={!!errors.zeroPauseMin} />
          {errors.zeroPauseMin ? <Err text={errors.zeroPauseMin} /> : <p className="field__hint">{Number.isInteger(minutes) && minutes >= 0 ? `Hoje: ${minutesLabel(minutes)}. Evita pausar e reativar quando o estoque oscila entre 1 e 0.` : ""}</p>}
        </div>
        <div className="field">
          <Label htmlFor="mlr-photo">Copiar as fotos e a capa de qual canal</Label>
          <Select id="mlr-photo" value={d.photoSource} onChange={(e) => setD({ ...d, photoSource: e.target.value })}>
            {data.photo_source_options.map((o) => <option key={o.slug} value={o.slug}>{o.name}</option>)}
            {d.photoSource && !data.photo_source_options.some((o) => o.slug === d.photoSource) ? <option value={d.photoSource}>{d.photoSource}</option> : null}
          </Select>
          <Err text={errors.photoSource} />
        </div>
      </div>
    </RuleCard>
  );
}

// ---------------------------------------------------------------------------
function DefaultsCard({ slug, data }: { slug: string; data: MlRulesResponse }): JSX.Element {
  const saved = defaultsToDraft(data.config);
  const [d, setD] = useState<DefaultsDraft>(saved);
  useEffect(() => setD(defaultsToDraft(data.config)), [data.config]);
  const save = useRulesSave(slug, "Padrões salvos");
  const errors = validateDefaults(d);
  const dirty = JSON.stringify(d) !== JSON.stringify(saved);
  const set = <K extends keyof DefaultsDraft>(k: K, v: string): void => setD((x) => ({ ...x, [k]: v }));
  return (
    <RuleCard
      title="Padrões quando nada mais preenche"
      hint="Valor usado quando o produto, o cadastro, o Sisplan e a categoria não dizem nada. Quem manda primeiro é sempre o que está mais perto do produto."
      dirty={dirty}
      invalid={Object.keys(errors).length > 0}
      saving={save.isPending}
      onSave={() => save.mutate(defaultsPatch(d, data.config))}
    >
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="field">
          <Label htmlFor="mlr-gen">Gênero</Label>
          <Select id="mlr-gen" value={d.ml_gender} onChange={(e) => set("ml_gender", e.target.value)}>
            <option value="">(nenhum)</option>
            {(data.options?.genders ?? GENDERS).map((g) => <option key={g} value={g}>{g}</option>)}
          </Select>
        </div>
        <div className="field">
          <Label htmlFor="mlr-age">Faixa etária</Label>
          <Select id="mlr-age" value={d.ml_age_group} onChange={(e) => set("ml_age_group", e.target.value)}>
            <option value="">(nenhuma)</option>
            {(data.options?.age_groups ?? AGE_GROUPS).map((g) => <option key={g} value={g}>{g}</option>)}
          </Select>
        </div>
        <div className="field">
          <Label htmlFor="mlr-list">Tipo de anúncio</Label>
          <Select id="mlr-list" value={d.ml_listing_type} onChange={(e) => set("ml_listing_type", e.target.value)}>
            <option value="">(Clássico)</option>
            {LISTING_TYPES.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {(["ml_pkg_height_cm", "ml_pkg_width_cm", "ml_pkg_length_cm", "ml_pkg_weight_g"] as const).map((k) => (
          <div className="field" key={k}>
            <Label htmlFor={`mlr-${k}`}>{PKG_LABEL[k]}</Label>
            <Input id={`mlr-${k}`} type="number" inputMode="numeric" min={1} step={1} value={d[k]} onChange={(e) => set(k, e.target.value)} aria-invalid={!!errors[k]} />
            <Err text={errors[k]} />
          </div>
        ))}
      </div>
      <p className="field__hint" style={{ margin: 0 }}>Embalagem: vale quando nem o produto nem a categoria têm a deles. O Sisplan ainda não traz peso nem medidas.</p>
    </RuleCard>
  );
}

// ---------------------------------------------------------------------------
function TermsCard({ slug, data }: { slug: string; data: MlRulesResponse }): JSX.Element {
  const saved = termsToDraft(data.config);
  const [d, setD] = useState<TermsDraft>(saved);
  useEffect(() => setD(termsToDraft(data.config)), [data.config]);
  const save = useRulesSave(slug, "Marca, garantia e prazo salvos");
  const errors = validateTerms(d);
  const dirty = JSON.stringify(d) !== JSON.stringify(saved);
  const set = <K extends keyof TermsDraft>(k: K, v: string): void => setD((x) => ({ ...x, [k]: v }));
  return (
    <RuleCard
      title="Marca, garantia e prazo de produção"
      hint="Aparecem em todo anúncio novo ou republicado. Campo vazio volta ao padrão do sistema."
      dirty={dirty}
      invalid={Object.keys(errors).length > 0}
      saving={save.isPending}
      onSave={() => save.mutate(termsPatch(d))}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="field">
          <Label htmlFor="mlr-brand">Marca no anúncio</Label>
          <Input id="mlr-brand" value={d.ml_brand} onChange={(e) => set("ml_brand", e.target.value)} placeholder="Hiit Fitwear" />
          <Err text={errors.ml_brand} />
        </div>
        <div className="field">
          <Label htmlFor="mlr-mf">Prazo de manuseio ou produção (dias)</Label>
          <Input id="mlr-mf" type="number" inputMode="numeric" min={1} max={90} step={1} value={d.ml_manufacturing_time_days} onChange={(e) => set("ml_manufacturing_time_days", e.target.value)} aria-invalid={!!errors.ml_manufacturing_time_days} />
          {errors.ml_manufacturing_time_days ? <Err text={errors.ml_manufacturing_time_days} /> : <p className="field__hint">Dias para preparar e despachar. Deixe vazio se despacha na hora.</p>}
        </div>
        <div className="field">
          <Label htmlFor="mlr-wt">Tipo de garantia</Label>
          <Input id="mlr-wt" value={d.ml_warranty_type} onChange={(e) => set("ml_warranty_type", e.target.value)} placeholder="Garantia do vendedor" />
          <Err text={errors.ml_warranty_type} />
        </div>
        <div className="field">
          <Label htmlFor="mlr-wtime">Tempo de garantia</Label>
          <Input id="mlr-wtime" value={d.ml_warranty_time} onChange={(e) => set("ml_warranty_time", e.target.value)} placeholder="30 dias" />
          <Err text={errors.ml_warranty_time} />
        </div>
      </div>
    </RuleCard>
  );
}

// ---------------------------------------------------------------------------
function StockCard({ slug, data }: { slug: string; data: MlRulesResponse }): JSX.Element {
  const savedMode = unpublishModeOf(data.config);
  const savedSafety = data.config.ml_stock_safety_units === undefined ? "" : String(data.config.ml_stock_safety_units);
  const [mode, setMode] = useState<"paused" | "closed">(savedMode);
  const [safety, setSafety] = useState(savedSafety);
  useEffect(() => {
    setMode(unpublishModeOf(data.config));
    setSafety(data.config.ml_stock_safety_units === undefined ? "" : String(data.config.ml_stock_safety_units));
  }, [data.config]);
  const save = useRulesSave(slug, "Regras de estoque salvas");
  const safetyErr = validateSafety(safety);
  const dirty = mode !== savedMode || safety !== savedSafety;
  const hint = UNPUBLISH_MODES.find((m) => m.value === mode)?.hint ?? "";
  return (
    <RuleCard
      title="Tirar do ar e guardar estoque"
      hint="O que fazer quando um produto sai do ar e quantas peças ficam fora do Mercado Livre para não vender o que não tem."
      dirty={dirty}
      invalid={!!safetyErr}
      saving={save.isPending}
      onSave={() =>
        save.mutate({ config: { ml_unpublish_mode: mode === "paused" ? null : "closed", ...safetyPatch(safety).config } })
      }
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="field">
          <Label htmlFor="mlr-mode">Quando tirar o produto do ar</Label>
          <Select id="mlr-mode" value={mode} onChange={(e) => setMode(e.target.value as "paused" | "closed")}>
            {UNPUBLISH_MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
          </Select>
          <p className="field__hint">{hint}</p>
        </div>
        <div className="field">
          <Label htmlFor="mlr-safe">Guardar quantas peças fora do Mercado Livre</Label>
          <Input id="mlr-safe" type="number" inputMode="numeric" min={0} max={1000} step={1} value={safety} onChange={(e) => setSafety(e.target.value)} placeholder={String(data.stock_safety_global)} aria-invalid={!!safetyErr} />
          {safetyErr ? <Err text={safetyErr} /> : <p className="field__hint">Vazio usa o valor geral do sistema ({data.stock_safety_global}). Vale só para este canal.</p>}
        </div>
      </div>
    </RuleCard>
  );
}

// ---------------------------------------------------------------------------
/** Linhas editaveis chave -> valor (marca do Sisplan -> marca no anuncio; grupo do Sisplan -> tipo da peca). */
function PairEditor({
  idPrefix,
  rows,
  setRows,
  keyLabel,
  valueLabel,
  keyPlaceholder,
  valuePlaceholder,
}: {
  idPrefix: string;
  rows: PairRow[];
  setRows: (r: PairRow[]) => void;
  keyLabel: string;
  valueLabel: string;
  keyPlaceholder: string;
  valuePlaceholder: string;
}): JSX.Element {
  const set = (i: number, patch: Partial<PairRow>): void => setRows(rows.map((r, k) => (k === i ? { ...r, ...patch } : r)));
  return (
    <div className="stack" style={{ gap: ".375rem" }}>
      {rows.map((r, i) => (
        <div key={i} style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1.4fr) auto", gap: ".5rem", alignItems: "end" }}>
          <div className="field">
            {i === 0 ? <Label htmlFor={`${idPrefix}-k${i}`}>{keyLabel}</Label> : null}
            <Input id={`${idPrefix}-k${i}`} aria-label={`${keyLabel} ${i + 1}`} value={r.key} onChange={(e) => set(i, { key: e.target.value })} placeholder={keyPlaceholder} />
          </div>
          <div className="field">
            {i === 0 ? <Label htmlFor={`${idPrefix}-v${i}`}>{valueLabel}</Label> : null}
            <Input id={`${idPrefix}-v${i}`} aria-label={`${valueLabel} ${i + 1}`} value={r.value} onChange={(e) => set(i, { value: e.target.value })} placeholder={valuePlaceholder} />
          </div>
          <Button type="button" variant="outline" size="sm" onClick={() => setRows(rows.filter((_, k) => k !== i))} aria-label={`Remover linha ${i + 1}`}>
            Remover
          </Button>
        </div>
      ))}
      <div>
        <Button type="button" variant="secondary" size="sm" onClick={() => setRows([...rows, { key: "", value: "" }])}>
          Adicionar linha
        </Button>
      </div>
    </div>
  );
}

function SisplanCard({ slug, data }: { slug: string; data: MlRulesResponse }): JSX.Element {
  const savedAud = audienceToRows(data.config);
  const savedBrand = mapToRows(data.config.ml_brand_map);
  const savedTypes = mapToRows(data.config.ml_group_piece_types);
  const savedFromName = data.config.ml_model_from_name === true;
  const [aud, setAud] = useState<AudienceRow[]>(savedAud);
  const [brands, setBrands] = useState<PairRow[]>(savedBrand);
  const [types, setTypes] = useState<PairRow[]>(savedTypes);
  const [fromName, setFromName] = useState(savedFromName);
  useEffect(() => {
    setAud(audienceToRows(data.config));
    setBrands(mapToRows(data.config.ml_brand_map));
    setTypes(mapToRows(data.config.ml_group_piece_types));
    setFromName(data.config.ml_model_from_name === true);
  }, [data.config]);
  const save = useRulesSave(slug, "Regras do Sisplan salvas");
  const b = rowsToMap(brands);
  const t = rowsToMap(types);
  const dupMsg = [...b.duplicates.map((k) => `marca "${k}" repetida`), ...t.duplicates.map((k) => `grupo "${k}" repetido`)].join("; ");
  const dirty =
    JSON.stringify(aud) !== JSON.stringify(savedAud) ||
    JSON.stringify(b.map) !== JSON.stringify(rowsToMap(savedBrand).map) ||
    JSON.stringify(t.map) !== JSON.stringify(rowsToMap(savedTypes).map) ||
    fromName !== savedFromName;
  const genders = data.options?.genders ?? GENDERS;
  const ages = data.options?.age_groups ?? AGE_GROUPS;
  return (
    <RuleCard
      title="O que vem do Sisplan"
      hint="O Sisplan já sabe o público, a marca e o tipo de cada peça. Aqui você diz como cada um vira dado do Mercado Livre. O que for escrito no produto, no canal, sempre vence."
      dirty={dirty}
      invalid={dupMsg !== ""}
      saving={save.isPending}
      onSave={() => {
        const cfg: Record<string, unknown> = {
          ...audiencePatch(aud).config,
          ...mapPatch("ml_brand_map", b.map).config,
          ...mapPatch("ml_group_piece_types", t.map).config,
          ml_model_from_name: fromName,
        };
        save.mutate({ config: cfg });
      }}
    >
      <fieldset className="stack field-group">
        <legend>Público: gênero e faixa etária</legend>
        {aud.length === 0 ? <p className="field__hint" style={{ margin: 0 }}>Nenhum tipo cadastrado. Sem isso, o gênero vem da categoria ou do padrão do canal.</p> : null}
        {aud.map((r, i) => (
          <div key={r.code} style={{ display: "grid", gridTemplateColumns: "minmax(5rem,1fr) minmax(0,1.2fr) minmax(0,1.2fr)", gap: ".5rem", alignItems: "end" }}>
            <span style={{ fontWeight: 700 }}>{r.label}</span>
            <div className="field">
              {i === 0 ? <Label htmlFor={`mls-g${i}`}>Gênero no ML</Label> : null}
              <Select id={`mls-g${i}`} aria-label={`Gênero no ML para ${r.label}`} value={r.gender} onChange={(e) => setAud(aud.map((x, k) => (k === i ? { ...x, gender: e.target.value } : x)))}>
                <option value="">(não usar)</option>
                {genders.map((g) => <option key={g} value={g}>{g}</option>)}
              </Select>
            </div>
            <div className="field">
              {i === 0 ? <Label htmlFor={`mls-a${i}`}>Faixa etária no ML</Label> : null}
              <Select id={`mls-a${i}`} aria-label={`Faixa etária no ML para ${r.label}`} value={r.age_group} onChange={(e) => setAud(aud.map((x, k) => (k === i ? { ...x, age_group: e.target.value } : x)))}>
                <option value="">(não usar)</option>
                {ages.map((g) => <option key={g} value={g}>{g}</option>)}
              </Select>
            </div>
          </div>
        ))}
      </fieldset>

      <fieldset className="stack field-group">
        <legend>Marca</legend>
        <p className="field__hint" style={{ margin: 0 }}>Marca que o Sisplan guarda no produto e como ela aparece no anúncio. Marca que não estiver aqui usa a marca padrão do canal.</p>
        <PairEditor idPrefix="mlb" rows={brands} setRows={setBrands} keyLabel="Marca no Sisplan" valueLabel="Marca no anúncio" keyPlaceholder="HIIT BEACHWEAR" valuePlaceholder="Hiit Beachwear" />
      </fieldset>

      <fieldset className="stack field-group">
        <legend>Tipo da peça no título</legend>
        <p className="field__hint" style={{ margin: 0 }}>O código é o do grupo no Sisplan (ex.: 07 é Legging). O tipo entra no começo do título quando o nome do produto está todo em maiúsculas ou tem poucas palavras. O tipo escrito na categoria vence este.</p>
        <PairEditor idPrefix="mlt" rows={types} setRows={setTypes} keyLabel="Código do grupo no Sisplan" valueLabel="Tipo da peça" keyPlaceholder="07" valuePlaceholder="Legging" />
      </fieldset>

      <Switch checked={fromName} onChange={(e) => setFromName(e.target.checked)} label={fromName ? "O modelo nasce do nome do produto" : "O modelo vem da categoria"} />
      <p className="field__hint" style={{ margin: 0 }}>Ligado, o modelo do anúncio é o nome do produto sem o tipo da peça (ex.: "LEGGING RIB" vira "Rib"). Desligado, todo produto da categoria leva o mesmo modelo.</p>
      {dupMsg ? <Err text={`Corrija: ${dupMsg}.`} /> : null}
    </RuleCard>
  );
}

// ---------------------------------------------------------------------------
function ShippingCard({ slug, data }: { slug: string; data: MlRulesResponse }): JSX.Element {
  const saved = shippingToDraft(data.config);
  const [d, setD] = useState<ShippingDraft>(saved);
  useEffect(() => setD(shippingToDraft(data.config)), [data.config]);
  const save = useRulesSave(slug, "Envio salvo");
  const errors = validateShipping(d);
  return (
    <RuleCard
      title="Envio"
      hint={`O envio é pelo Mercado Envios. A partir de R$ ${ML_FREE_SHIPPING_FLOOR} o Mercado Livre obriga o frete grátis e essa regra dele vale sempre. Aqui você escolhe a retirada na loja e, se quiser, o frete grátis também abaixo desse valor.`}
      dirty={JSON.stringify(d) !== JSON.stringify(saved)}
      invalid={Object.keys(errors).length > 0}
      saving={save.isPending}
      onSave={() => save.mutate(shippingPatch(d))}
    >
      <Switch checked={d.pickup} onChange={(e) => setD({ ...d, pickup: e.target.checked })} label={d.pickup ? "Aceita retirada na loja" : "Sem retirada na loja"} />
      <p className="field__hint" style={{ margin: 0 }}>A retirada vale só se o endereço da loja estiver ativo para retirada na conta do Mercado Livre (como no site: Barueri-SP). Ligue depois de confirmar lá.</p>
      <div className="field">
        <Label htmlFor="mlr-free">Oferecer frete grátis a partir de (R$)</Label>
        <Input id="mlr-free" inputMode="decimal" value={d.freeFrom} onChange={(e) => setD({ ...d, freeFrom: e.target.value })} placeholder="vazio = só o que o Mercado Livre obriga" aria-invalid={!!errors.freeFrom} />
        {errors.freeFrom ? <Err text={errors.freeFrom} /> : <p className="field__hint">Vale só para anúncios abaixo de R$ {ML_FREE_SHIPPING_FLOOR}: o custo do frete grátis é da loja.</p>}
      </div>
    </RuleCard>
  );
}

// ---------------------------------------------------------------------------
function GtinCard({ slug, data }: { slug: string; data: MlRulesResponse }): JSX.Element {
  const saved = gtinToDraft(data.config);
  const [d, setD] = useState<GtinDraft>(saved);
  useEffect(() => setD(gtinToDraft(data.config)), [data.config]);
  const save = useRulesSave(slug, "Código de barras salvo");
  const err = validateGtin(d);
  return (
    <RuleCard
      title="Código de barras (EAN)"
      hint="O EAN do Sisplan vai como código de barras do anúncio. Os EANs de hoje começam com 777 (não são da faixa brasileira 789/790); o Mercado Livre não recusou nos testes. Se um dia recusar, desligue aqui: o anúncio sobe com o aviso “sem código”."
      dirty={JSON.stringify(d) !== JSON.stringify(saved)}
      invalid={!!err}
      saving={save.isPending}
      onSave={() => save.mutate(gtinPatch(d))}
    >
      <Switch checked={d.send} onChange={(e) => setD({ ...d, send: e.target.checked })} label={d.send ? "Enviar o EAN do Sisplan" : "Não enviar o EAN (usar “sem código”)"} />
      <div className="field">
        <Label htmlFor="mlr-gtin">Enviar só EAN que começa com (opcional)</Label>
        <Input id="mlr-gtin" value={d.prefixes} onChange={(e) => setD({ ...d, prefixes: e.target.value })} placeholder="789, 790" aria-invalid={!!err} disabled={!d.send} />
        {err ? <Err text={err} /> : <p className="field__hint">Vazio envia todo EAN válido. Com números, só os EANs que começam por eles seguem; os outros vão “sem código”.</p>}
      </div>
    </RuleCard>
  );
}

// ---------------------------------------------------------------------------
function ColorsCard({ slug, data }: { slug: string; data: MlRulesResponse }): JSX.Element {
  const inUse = useMemo(() => data.colors_in_use ?? [], [data.colors_in_use]);
  const options = data.main_color_options ?? [];
  const saved = colorRows(data.config, inUse);
  const [rows, setRows] = useState<PairRow[]>(saved);
  useEffect(() => setRows(colorRows(data.config, inUse)), [data.config, inUse]);
  const save = useRulesSave(slug, "Cores salvas");
  const m = rowsToMap(rows);
  const savedMap = rowsToMap(saved).map;
  const mapped = Object.keys(m.map).length;
  return (
    <RuleCard
      title="Cores"
      hint="Cada cor do Sisplan precisa da “cor principal” do Mercado Livre (uma lista fechada de 16 cores) para o anúncio aparecer no filtro de cor. Cor sem equivalente fica em branco: o anúncio sobe, só sem esse filtro."
      dirty={JSON.stringify(m.map) !== JSON.stringify(savedMap)}
      invalid={false}
      saving={save.isPending}
      onSave={() => save.mutate(mapPatch("ml_main_color_map", m.map))}
    >
      <p className="field__hint" style={{ margin: 0 }}>{mapped} de {rows.length} cores com cor principal definida.</p>
      <details>
        <summary style={{ cursor: "pointer", fontWeight: 700 }}>Ver e editar as cores</summary>
        <div className="stack" style={{ gap: ".375rem", marginTop: ".5rem", maxHeight: "28rem", overflowY: "auto" }}>
          {rows.map((r, i) => (
            <div key={r.key} style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)", gap: ".5rem", alignItems: "center" }}>
              <label htmlFor={`mlc-c${i}`} style={{ fontWeight: 600 }}>{r.key}</label>
              {options.length > 0 ? (
                <Select id={`mlc-c${i}`} aria-label={`Cor principal do ML para ${r.key}`} value={r.value} onChange={(e) => setRows(rows.map((x, k) => (k === i ? { ...x, value: e.target.value } : x)))}>
                  <option value="">(sem cor principal)</option>
                  {options.map((o) => <option key={o} value={o}>{o}</option>)}
                  {r.value && !options.includes(r.value) ? <option value={r.value}>{r.value}</option> : null}
                </Select>
              ) : (
                <Input id={`mlc-c${i}`} aria-label={`Cor principal do ML para ${r.key}`} value={r.value} onChange={(e) => setRows(rows.map((x, k) => (k === i ? { ...x, value: e.target.value } : x)))} />
              )}
            </div>
          ))}
        </div>
      </details>
    </RuleCard>
  );
}

// ---------------------------------------------------------------------------
function AiPromptCard({ slug, data }: { slug: string; data: MlRulesResponse }): JSX.Element {
  const fallback = data.ai_prompt_default ?? "";
  const savedRaw = typeof data.config.ai_prompt === "string" ? data.config.ai_prompt : "";
  const saved = savedRaw || fallback;
  const [text, setText] = useState(saved);
  useEffect(() => setText(saved), [saved]);
  const save = useRulesSave(slug, "Jeito de escrever da IA salvo");
  const err = validateAiPrompt(text);
  return (
    <RuleCard
      title="Como a IA escreve para este canal"
      hint="Instrução fixa que vale para todo botão Gerar com IA deste canal (título, descrição, modelo). A IA só recebe dados verdadeiros do produto: composição e tecnologias vêm do cadastro, nunca inventadas."
      dirty={text.trim() !== saved.trim()}
      invalid={!!err}
      saving={save.isPending}
      onSave={() => save.mutate(aiPromptPatch(text, fallback))}
    >
      <div className="field">
        <Label htmlFor="mlr-ai">Instrução para a IA</Label>
        <Textarea id="mlr-ai" rows={6} value={text} onChange={(e) => setText(e.target.value)} aria-invalid={!!err} />
        {err ? <Err text={err} /> : <p className="field__hint">{text.length}/{AI_PROMPT_MAX} caracteres. Apague tudo e salve para voltar ao texto padrão.</p>}
      </div>
    </RuleCard>
  );
}

// ---------------------------------------------------------------------------
interface SettingsResponse { values: Record<string, unknown> }

function AdvancedCard(): JSX.Element {
  const qc = useQueryClient();
  const q = useQuery<SettingsResponse>({ queryKey: ["admin-settings"], queryFn: async () => (await api.get<SettingsResponse>("/api/admin/settings")).data });
  const [d, setD] = useState<Record<string, string>>({});
  useEffect(() => {
    if (!q.data) return;
    setD(Object.fromEntries(ADVANCED_SETTINGS.map((s) => [s.key, q.data!.values[s.key] === undefined ? "" : String(q.data!.values[s.key])])));
  }, [q.data]);
  const save = useMutation({
    mutationFn: async (body: Record<string, number>) => (await api.patch("/api/admin/settings", body)).data,
    onSuccess: () => {
      toast.success("Ajustes técnicos salvos");
      void qc.invalidateQueries({ queryKey: ["admin-settings"] });
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
  if (q.isLoading) return <Skeleton className="h-16 w-full" />;
  if (q.isError || !q.data) return <p className="field__hint" style={{ color: "var(--color-warn)" }}>Não deu para ler os ajustes técnicos ({apiErrorMessage(q.error)}).</p>;
  const errs = Object.fromEntries(ADVANCED_SETTINGS.map((s) => [s.key, d[s.key] === undefined ? null : validateAdvanced(s, d[s.key] ?? "")]));
  const changed = ADVANCED_SETTINGS.filter((s) => d[s.key] !== undefined && d[s.key] !== (q.data!.values[s.key] === undefined ? "" : String(q.data!.values[s.key])));
  const invalid = changed.some((s) => errs[s.key]);
  return (
    <details className="card">
      <summary style={{ cursor: "pointer", fontWeight: 800 }}>Avançado (técnico): só quem cuida do sistema</summary>
      <form
        className="stack"
        style={{ gap: "var(--sp-3)", marginTop: ".75rem" }}
        onSubmit={(e) => {
          e.preventDefault();
          if (!invalid && changed.length > 0) save.mutate(Object.fromEntries(changed.map((s) => [s.key, Number(d[s.key])])));
        }}
      >
        <p className="field__hint" style={{ margin: 0 }}>Frequência e limites dos processos automáticos do Mercado Livre. Mexa só se souber o efeito; o sistema já funciona com os valores atuais.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {ADVANCED_SETTINGS.map((s) => (
            <div className="field" key={s.key}>
              <Label htmlFor={`mla-${s.key}`}>{s.label}</Label>
              <Input id={`mla-${s.key}`} type="number" inputMode="numeric" min={s.min} max={s.max} step={1} value={d[s.key] ?? ""} onChange={(e) => setD((x) => ({ ...x, [s.key]: e.target.value }))} aria-invalid={!!(d[s.key] !== undefined && errs[s.key])} />
              {d[s.key] !== undefined && errs[s.key] ? <Err text={errs[s.key]!} /> : <p className="field__hint">{s.hint}</p>}
            </div>
          ))}
        </div>
        <div className="spread">
          <span className="field__hint">{changed.length > 0 ? `${changed.length} ajuste(s) não salvo(s).` : "Tudo salvo."}</span>
          <Button type="submit" disabled={changed.length === 0 || invalid || save.isPending} loading={save.isPending}>Salvar ajustes técnicos</Button>
        </div>
      </form>
    </details>
  );
}

// ---------------------------------------------------------------------------
export function MlRulesTab({ slug }: { slug: string }): JSX.Element {
  const { can } = useAuth();
  const q = useQuery<MlRulesResponse>({
    queryKey: rulesKey(slug),
    queryFn: async () => (await api.get<MlRulesResponse>(`/api/channels/${slug}/ml/rules`)).data,
  });
  if (!can("channel_rules.write")) {
    return <p className="field__hint">Você não tem acesso às regras do Mercado Livre. Peça a um administrador.</p>;
  }
  if (q.isLoading) return <Skeleton className="h-64 w-full" />;
  if (q.isError || !q.data) {
    return <p className="field__hint" style={{ color: "var(--color-warn)" }}>Não deu para ler as regras ({apiErrorMessage(q.error)}).</p>;
  }
  const data = q.data;
  return (
    <div className="stack" style={{ gap: "var(--sp-4)", minWidth: 0 }}>
      <p className="field__hint" style={{ margin: 0 }}>
        Aqui ficam todas as regras do Mercado Livre. Salvar uma regra não republica nada sozinho: os produtos pegam a regra nova na próxima publicação ou republicação.
      </p>
      <AutoPublishCard slug={slug} data={data} />
      <DefaultsCard slug={slug} data={data} />
      <TermsCard slug={slug} data={data} />
      <StockCard slug={slug} data={data} />
      <SisplanCard slug={slug} data={data} />
      <ShippingCard slug={slug} data={data} />
      <GtinCard slug={slug} data={data} />
      <ColorsCard slug={slug} data={data} />
      <AiPromptCard slug={slug} data={data} />
      <section className="card stack" style={{ gap: "var(--sp-3)", minWidth: 0 }} aria-label="Categorias e tamanhos">
        <div className="stack" style={{ gap: ".25rem" }}>
          <h3 className="card__title" style={{ margin: 0 }}>Categorias, ficha técnica e tamanhos</h3>
          <p className="field__hint" style={{ margin: 0 }}>
            Cada categoria do Vitrine aponta para uma do Mercado Livre. Produto sem categoria mapeada não é publicado.
          </p>
        </div>
        <MlCategoryMapPanel slug={slug} />
      </section>
      {can("settings.manage") ? <AdvancedCard /> : null}
    </div>
  );
}
